import React from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { Container } from '../../../../types/canvas.types';
import { DragIcon, MultiChoiceIcon, DownArrow, UpArrow } from 'assests/GmsIcons';
import { OverFlowIcon } from 'assests/GmsIcons';
import { DropdownMenu } from '../../../Common/DropdownMenu';
import { WidgetIcon, DeleteIcon } from '../utils/containerIcons';
import { getItemDisplayName, getItemTypeLabel, truncateName } from '../utils/containerHelpers';
import { NestedContainerItem } from './NestedContainerItem';

interface ContainerContentsListProps {
  id?: string;
  selectedContainer: Container | null;
  isContentsExpanded: boolean;
  setIsContentsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  handleItemClick: (item: any, containerId: string) => void;
  handleNestedContainerClick: (nestedContainerId: string) => void;
  handleSubmodelClick: (submodelId: string, submodelName: string) => void;
  handleDeleteComponent: (item: any, containerId: string) => void;
  handleDeleteNestedContainer: (nestedContainer: Container) => void;
  handleOnDragEnd: (result: DropResult) => void;
  handleNestedItemDragEnd: (result: DropResult, nestedContainerId: string) => void;
}

export const ContainerContentsList: React.FC<ContainerContentsListProps> = ({
  selectedContainer,
  isContentsExpanded,
  setIsContentsExpanded,
  handleItemClick,
  handleNestedContainerClick,
  handleSubmodelClick,
  handleDeleteComponent,
  handleDeleteNestedContainer,
  handleOnDragEnd,
  handleNestedItemDragEnd
}) => {
  const styles = {
    header: 'tw-flex tw-justify-between tw-items-center tw-cursor-pointer tw-p-3 tw-bg-gray-50 hover:tw-bg-gray-100 tw-rounded',
    headerTitle: 'tw-text-[#6D788C] tw-font-semibold tw-text-sm',
    collapseButton: 'tw-p-1 tw-rounded hover:tw-bg-gray-200'
  };

  return (
    <div className="tw-border-t tw-border-[#E5E7EB] tw-pb-10">
      <div
        className={styles.header}
        onClick={() => setIsContentsExpanded(!isContentsExpanded)}
      >
        <h3 className={styles.headerTitle}>Added Components or Container</h3>
        <button
          className={styles.collapseButton}
          onClick={(e) => {
            e.stopPropagation();
            setIsContentsExpanded(!isContentsExpanded);
          }}
        >
          {isContentsExpanded ? <UpArrow /> : <DownArrow />}
        </button>
      </div>

      {isContentsExpanded && selectedContainer && (
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <div className="tw-mt-2">
            
            {/* Show Components with Drag and Drop */}
            {selectedContainer.content.items && selectedContainer.content.items.length > 0 && (
              <div className="tw-mb-4">
                <Droppable droppableId="components" type="component">
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`tw-space-y-1 tw-ps-4 ${
                        snapshot.isDraggingOver ? 'tw-bg-blue-50 tw-rounded' : ''
                      }`}
                    >
                      {selectedContainer?.content?.items?.map((item: any, index: number) => {
                        const displayName = getItemDisplayName(item);
                        const typeLabel = getItemTypeLabel(item);
                        const fullName = `${displayName} ${typeLabel}`;

                        return (
                          <Draggable
                            key={`${item.id}`}
                            draggableId={`${item.id}`}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-rounded tw-cursor-pointer hover:tw-bg-gray-50 tw-group ${
                                  snapshot.isDragging ? 'tw-bg-blue-100 tw-shadow-lg' : ''
                                }`}
                                onClick={() => handleItemClick(item, selectedContainer.id)}
                              >
                                <div {...provided.dragHandleProps} className="tw-cursor-move">
                                  <DragIcon />
                                </div>
                                <div className="tw-flex-shrink-0">
                                  {item.type === 'widget' ? <WidgetIcon /> : <MultiChoiceIcon />}
                                </div>
                                <div className="tw-flex-1 tw-min-w-0">
                                  <span
                                    className="tw-text-sm tw-text-[#172B4D] tw-block tw-truncate"
                                    title={fullName}
                                  >
                                    {truncateName(displayName)}
                                    {typeLabel && (
                                      <span className="tw-text-xs tw-text-[#97A0AF] tw-ml-1">
                                        {typeLabel}
                                      </span>
                                    )}
                                  </span>
                                </div>
                                <div className="">
                                  <DropdownMenu
                                    trigger={<OverFlowIcon />}
                                    position="right"
                                    items={[
                                      {
                                        id: 'delete',
                                        label: 'Delete',
                                        icon: <DeleteIcon />,
                                        onClick: () => handleDeleteComponent(item, selectedContainer.id),
                                        className: 'tw-text-red-600 hover:tw-bg-red-50'
                                      }
                                    ]}
                                  />
                                </div>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            )}

            {/* Nested Containers with Drag and Drop */}
            {selectedContainer.content.nestedContainers && selectedContainer.content.nestedContainers.length > 0 && (
              <div>
                <div className="tw-text-xs tw-font-semibold tw-text-[#6D788C] tw-mb-2 tw-ps-3">Nested Containers</div>
                <Droppable droppableId="nested-containers" type="nested-container">
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`tw-space-y-1 ${
                        snapshot.isDraggingOver ? 'tw-bg-blue-50 tw-rounded tw-p-2' : ''
                      }`}
                    >
                      {selectedContainer?.content?.nestedContainers?.map((nestedContainer: Container, index: number) => (
                        <Draggable
                          key={nestedContainer.id}
                          draggableId={nestedContainer.id}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={snapshot.isDragging ? 'tw-opacity-75' : ''}
                            >
                              <NestedContainerItem
                                nestedContainer={nestedContainer}
                                level={1}
                                onItemClick={handleItemClick}
                                onNestedContainerClick={handleNestedContainerClick}
                                onSubmodelClick={handleSubmodelClick}
                                onDeleteComponent={handleDeleteComponent}
                                onDeleteNestedContainer={handleDeleteNestedContainer}
                                dragHandleProps={provided.dragHandleProps}
                                isDragging={snapshot.isDragging}
                                onItemDragEnd={handleNestedItemDragEnd}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            )}

            {/* Empty state */}
            {!selectedContainer.content.submodel &&
              (!selectedContainer.content.items || selectedContainer.content.items.length === 0) &&
              (!selectedContainer.content.nestedContainers || selectedContainer.content.nestedContainers.length === 0) && (
              <div className="tw-text-sm tw-text-[#97A0AF] tw-text-center tw-py-4">
                No contents in this container
              </div>
            )}
          </div>
        </DragDropContext>
      )}
    </div>
  );
};
